import {
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import { MatMenuTrigger } from '@angular/material/menu';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ITypeWithId } from '@shared/types/common.types';
import { asyncScheduler, BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, distinctUntilChanged, filter, switchMap, throttleTime } from 'rxjs/operators';

// export interface ISearchResultItem {
//   id: number;
//   name: string;
//   aditionalProps?: any;
//   selected?: boolean
// }

export interface ISearchResultItemGeneric<T> {
  id: T;
  name: string;
  selected?: boolean;
  additionalProps?: any;
  isLoading?: boolean
}

export type SearchFunction<T> = (v: string) => Observable<ISearchResultItemGeneric<T>[]>;

@UntilDestroy()
@Component({
  selector: 'app-multiple-select-with-search',
  templateUrl: './multiple-select-with-search.component.html',
  styleUrls: [ './multiple-select-with-search.component.scss' ],
})
export class MultipleSelectWithSearchComponent<T extends number | string> implements OnInit, OnChanges {
  @Input() searchFn: SearchFunction<T>;
  // @Input() valueStream$: Observable<ISearchResultItemGeneric<T>>;
  @Input() selectedItems: ISearchResultItemGeneric<T>[];
  @Input() control: UntypedFormControl;
  @Input() showChips: boolean;
  @Input() showChangedCount = true;
  @Input() limitChipsName: number;
  @Input() limitOptionText: number;
  @Input() showAnimations: boolean;
  @Input() clearSearchTermAndSelection: boolean;
  @Input() placeholder: string = $localize`Поиск`;
  /** only controlPropName property of selected ISearchResultItem will be set to control value **/
  @Input() controlPropName: 'id' | 'name';
  @Input() identifierProp: 'id' | 'idString' = 'id';
  @Input() closeTrigger$: Observable<any>;
  @Input() label: string;
  @Input() required: boolean;
  @Input() disabled: boolean;
  @Input() triggerIcon = 'keyboard_arrow_down';
  @Output() selectionChanged = new EventEmitter<ISearchResultItemGeneric<T>[]>();

  // @ViewChild('input') autocompleteTrigger: MatAutocompleteTrigger;
  // @ViewChild('autocomplete') autocomplete: MatAutocomplete;
  @ViewChild('panelMenuTrigger') panelMenuTrigger: MatMenuTrigger;
  @ViewChild('labelElement') labelElement: ElementRef<HTMLElement>;
  searchControl = new UntypedFormControl();
  list$ = new BehaviorSubject<ISearchResultItemGeneric<T>[]>([ ]);
  invalid = false;
  isLoading = false;
  searchHasBeenDone = false;
  _showPanel: boolean;
  get showPanel() {
    return this._showPanel;
  }
  set showPanel(val: boolean) {
    this.onSetShowPanel(val);
  };

  constructor() {
    if (this.required) {
      this.searchControl.setValidators(Validators.required);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.disabled) {
      if (this.disabled) {
        this.searchControl.disable();
      } else {
        this.searchControl.enable();
      }
    }
    if (changes.selectedItems) {
      this.applySelectedItemsToList()
    }
  }

  ngOnInit(): void {
    if (this.closeTrigger$) {
      this.closeTrigger$.pipe(untilDestroyed(this)).subscribe({
        next: () => {
          this.closePanel();
        },
      });
    }

    // if (this.control) {
    //   this.control.valueChanges.pipe(untilDestroyed(this)).subscribe({
    //     next: (data) => {
    //       if (data === this.controlValueChangedInsideComponent) {
    //         // console.log('NOOOOOOO');
    //       } else {
    //         this.applyMultipleControlValueToList();
    //       }
    //     }
    //   })
    // }

    if (this.searchFn) {
      this.subscribeToSeachFn();
    }
  }

  private applySelectedItemsToList() {
    // console.log('Apply to list', this.selectedItems, this.list$.getValue());
    if (this.selectedItems?.length) {
      const value = this.selectedItems;
      if (!value[0].id) {
        console.error('selectedItems must contain array of elements of ITypeWithId type');
        return;
      }
      if (this.list$.getValue()?.length) {
        this.changeSelectedStateInList(this.list$.getValue(), value);
      }
    } else if (this.selectedItems?.length === 0) {
      if (this.list$.getValue()?.length) {
        this.changeSelectedStateInList(this.list$.getValue(), []);
      }
    }
  }

  private changeSelectedStateInList(list: ISearchResultItemGeneric<T>[], selectedList: ITypeWithId[]) {
    list.forEach(item => {
      item.selected = selectedList.findIndex(val => val.id === item.id) >= 0;
    });
  }

  private subscribeToSeachFn() {
    this.searchControl.valueChanges
      .pipe(
        untilDestroyed(this),
        filter((v) => {
          if (v?.id) {
            // console.log('Why we use this filter', v);
          }
          return !v?.id
        }),
        distinctUntilChanged(),
        throttleTime(400, asyncScheduler, { leading: true, trailing: true }),
        switchMap((v) => {
          this.list$.next([]);
          this.isLoading = true;
          return this.searchFn(v);
        }),
        catchError((e) => {
          this.isLoading = false;
          return of([]);
        }),
      )
      .subscribe({
        next: (data) => {
          if (data?.length === 1 && data[0].isLoading) {
            this.isLoading = true;
          } else {
            this.searchHasBeenDone = true;
            this.isLoading = false;
            data = data.map(item => ({
              ...item,
              selected: this.isItemSelected(item)
            }))
            this.list$.next(data);
          }
        },
      });
  }

  private isItemSelected(item: ISearchResultItemGeneric<T>) {
    if (!this.selectedItems) {
      return false;
    }
    const selectedValues: ITypeWithId[] = this.selectedItems;
    if (!Array.isArray(selectedValues)) {
      console.error('Control value is not array', selectedValues);
      return false;
    }
    return selectedValues.findIndex(val => val.id === item.id) >= 0;
  }

  setInvalid() {
    this.invalid = true;
  }

  iconClick($event) {
    $event.preventDefault();
    $event.stopPropagation();

    if (this.searchControl.value) {
      this.searchControl.setValue('');
      if (this.clearSearchTermAndSelection) {
        this.removeAll();
      }
      return;
    }
    if (this.clearSearchTermAndSelection && this.selectedItems.length) {
      this.removeAll();
    }
    this.fieldClick();
  }

  fieldClick(clickToOpen = false) {
    if (this.disabled) { return; }

    this.searchControl.setValue(this.searchControl.value);
    if (clickToOpen) {
      // "clickToOpen" is needed because without it, a double function call
      // is triggered (I could not understand why)
      this.openPanel();
    } else {
      this.showPanel = !this.showPanel;
    }
  }

  private openPanel() {
    this.showPanel = true;
  }

  private closePanel() {
    this.showPanel = false;
  }

  checkboxChange(event: MatCheckboxChange, item: ISearchResultItemGeneric<T>) {
    item.selected = event.checked;
    if (event.checked) {
      this.addItemToSelectedList(item);
    } else {
      this.removeItemFromSelectedList(item);
    }
    this.selectionChanged.emit(this.selectedItems);
  }

  removeChips(item: ISearchResultItemGeneric<T>) {
    item.selected = false;
    this.removeItemFromSelectedList(item);
    this.selectionChanged.emit(this.selectedItems);
  }

  removeAll() {
    this.selectedItems.forEach(p => p.selected = false);
    this.selectedItems = [];
    this.selectionChanged.emit(this.selectedItems);
  }

  private addItemToSelectedList(item: ISearchResultItemGeneric<T>) {
    const list: ISearchResultItemGeneric<T>[] = this.selectedItems;
    if (list.findIndex(val => val.id === item.id) < 0) {
      list.push(item);
    }
  }

  private removeItemFromSelectedList(item: ISearchResultItemGeneric<T>) {
    const list: ISearchResultItemGeneric<T>[] = this.selectedItems;
    const foundIndex = list.findIndex(val => val.id === item.id);
    if (foundIndex >= 0) {
      list.splice(foundIndex, 1);
    }
  }

  stopEvent(event: MouseEvent) {
    event.stopPropagation();
  }

  private onSetShowPanel(val: boolean) {
    this._showPanel = val;
    if (val) {
      this.panelMenuTrigger?.openMenu();
    } else {
      this.panelMenuTrigger?.closeMenu();
    }
  }
}
